import { Bell, Eye, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import { ClawbertMark } from "./ClawbertMark";
import { SectionHeading, SETTLE_SPRING } from "./SectionHeading";

const POINTS = [
  { icon: ShieldCheck, key: "pointOne" },
  { icon: Bell, key: "pointTwo" },
  { icon: Eye, key: "pointThree" },
] as const;

/**
 * Introductions — the third beat of the compounding arc (work → notes →
 * network), and the only pillar that is NOT live.
 *
 * Design note: the section is built around a mock of the ACTUAL EMAIL rather
 * than a description of the feature. "Imagine if your agent introduced you to
 * someone" asks the reader to do the work; showing the 6am email does it for
 * them, and it doubles as the honest spec — every line in that card traces to
 * a field we collect (context, need, offer, geography, mutual consent).
 *
 * The 6:04am timestamp is deliberate: it rhymes with the notebook card's
 * "written overnight · 2:14am" so the two sections read as one motif — the
 * agent works while you sleep, and you wake up to what it found.
 *
 * Stays in future tense throughout, carries a "Being built" badge, and offers
 * no opt-in control: the real opt-in lives in onboarding, and a button that
 * collected nothing would be a lie on a page whose whole claim is that it
 * doesn't pretend.
 */
export function SocialsSection() {
  const { t } = useTranslation("home");
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      id="introductions"
      className="scroll-mt-32 px-5 py-20 md:scroll-mt-24 md:py-24"
    >
      <div className="mx-auto max-w-5xl">
        <div className="flex justify-center">
          <span className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-home-ink/15 bg-home-cream px-3 py-1.5 font-body text-[12px] font-bold text-home-ink/65">
            <span className="h-1.5 w-1.5 rounded-full bg-home-yellow" />
            {t("socials.badge")}
          </span>
        </div>

        <SectionHeading kicker={t("socials.kicker")} title={t("socials.title")} />

        <p className="mx-auto mt-6 max-w-2xl text-center font-body text-[16px] leading-relaxed text-home-ink/75">
          {t("socials.lede")}
        </p>

        {/* The artifact: the email you'd actually wake up to. */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={SETTLE_SPRING}
          className="mx-auto mt-10 max-w-lg overflow-hidden rounded-[26px] bg-white shadow-[0_30px_66px_-32px_rgba(33,31,28,0.5)]"
        >
          <div className="flex items-center gap-3 border-b border-home-ink/8 px-5 py-4">
            <ClawbertMark className="h-8 w-8 shrink-0" pose="reading" />
            <div className="min-w-0 flex-1">
              <p className="font-body text-[14px] font-bold text-home-ink">
                {t("socials.emailFrom")}
              </p>
              <p className="truncate font-body text-[13px] text-home-ink/65">
                {t("socials.emailSubject")}
              </p>
            </div>
            <span className="shrink-0 font-body text-[12px] font-semibold text-home-ink/65">
              {t("socials.emailTime")}
            </span>
          </div>

          <div className="space-y-3 px-5 py-5">
            <p className="font-body text-[14px] leading-relaxed text-home-ink/70">
              {t("socials.emailBodyOne")}
            </p>
            <p className="font-body text-[14px] leading-relaxed text-home-ink">
              {t("socials.emailBodyTwo")}
            </p>
            <p className="font-body text-[14px] leading-relaxed text-home-ink/70">
              {t("socials.emailBodyThree")}
            </p>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <span className="rounded-full bg-home-yellow px-4 py-2 font-body text-[13px] font-bold text-home-ink">
                {t("socials.emailCta")}
              </span>
            </div>

            <p className="pt-1 font-body text-[12px] leading-relaxed text-home-ink/65">
              {t("socials.emailFootnote")}
            </p>
          </div>
        </motion.div>

        <div className="mx-auto mt-12 grid max-w-3xl gap-3 sm:grid-cols-3">
          {POINTS.map(({ icon: Icon, key }, i) => (
            <motion.div
              key={key}
              initial={reduced ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.06, ease: "easeOut" }}
              className="rounded-2xl border border-home-ink/10 bg-home-cream p-5 text-center"
            >
              <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-home-ink/[0.05]">
                <Icon className="h-4 w-4 text-home-ink/65" strokeWidth={2} />
              </span>
              <p className="mt-3 font-body text-[13.5px] leading-relaxed text-home-ink/70">
                {t(`socials.${key}`)}
              </p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
